import {
  NIAudioCloseIcon,
  NIAudioNextIcon,
  NIAudioPauseIcon,
  NIAudioPlayIcon,
  NIAudioPreviousIcon,
} from '@native-instruments/webex-ni';

import { clsx } from 'clsx';
import {
  type PropsWithChildren,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { createPortal } from 'react-dom';

type AudioPlayerContext = {
  currentPlaylist: string;
  isPlaying: boolean;
  startPlaylist: (playlistId: string, playlist: Playlist) => void;
};

export const AudioPlayerContext = createContext<AudioPlayerContext | undefined>(
  undefined,
);

export function useAudioPlayer() {
  const value = useContext(AudioPlayerContext);

  if (!value) {
    throw new Error('useAudioPlayer must be used within AudioPlayerContext');
  }

  return value;
}

type Track = {
  title: string;
  description: string;
  currentTime: string;
  uri: string;
  positionInPlaylist: number;
};

type Playlist = {
  title: string;
  description: string;
  uri: string;
}[];

export const AudioPlayerProvider = ({
  children,
  audioPlayerRef,
}: PropsWithChildren & { audioPlayerRef: RefObject<HTMLElement> }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(-1);
  const [currentTime, setCurrentTime] = useState(0);
  const [playlist, setPlaylist] = useState<Playlist>([]);
  const [playlistId, setPlaylistId] = useState<string>('');

  const currentTrack: Track | null = useMemo(() => {
    if (playlist.length <= 0 || currentTrackIndex <= -1) {
      return null;
    }

    return {
      ...playlist[currentTrackIndex],
      currentTime: new Date(currentTime * 1000).toISOString().substring(14, 19), // As "mm:ss"
      positionInPlaylist: currentTrackIndex + 1,
    };
  }, [playlist, currentTrackIndex, currentTime]);

  const startPlaylist = useCallback(
    (givenPlaylistId: string, givenPlaylist: Playlist) => {
      if (!audioRef.current || givenPlaylist.length <= 0) return;

      const isCurrentPlaylist = givenPlaylistId === playlistId;

      if (isCurrentPlaylist) {
        audioRef.current.paused
          ? audioRef.current.play()
          : audioRef.current.pause();
        setIsPlaying(!audioRef.current.paused);
        return;
      }

      setPlaylist(givenPlaylist);
      setPlaylistId(givenPlaylistId);
      setCurrentTrackIndex(0);
    },
    [playlistId],
  );

  useEffect(() => {
    if (!audioRef.current || playlist.length <= 0) return;

    audioRef.current.setAttribute('src', playlist[currentTrackIndex].uri);
    audioRef.current.load();

    audioRef.current.volume = 0.3;
    audioRef.current.play();

    setIsPlaying(true);
  }, [playlist, currentTrackIndex]);

  const prevTrack = () => {
    let newIndex = currentTrackIndex - 1;
    if (newIndex < 0) newIndex = playlist.length - 1;

    setCurrentTrackIndex(newIndex);
  };

  const nextTrack = () => {
    let newIndex = currentTrackIndex + 1;
    if (newIndex > playlist.length - 1) newIndex = 0;

    setCurrentTrackIndex(newIndex);
  };

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;

    audioRef.current.paused
      ? audioRef.current.play()
      : audioRef.current.pause();
    setIsPlaying(!audioRef.current.paused);
  }, []);

  const closePlaylist = () => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    audioRef.current.src = '';

    setPlaylist([]);
    setPlaylistId('');
    setIsPlaying(false);
    setCurrentTrackIndex(-1);
  };

  const onTimeUpdate = () => {
    if (!audioRef?.current) return;
    setCurrentTime(audioRef?.current?.currentTime);
  };

  return (
    <AudioPlayerContext.Provider
      value={{
        startPlaylist,
        currentPlaylist: playlistId,
        isPlaying,
      }}
    >
      {children}

      <audio ref={audioRef} src="" onTimeUpdate={onTimeUpdate}>
        <track src={currentTrack?.uri} kind="captions" />
      </audio>

      {audioPlayerRef.current &&
        currentTrack &&
        createPortal(
          <AudioPlayer
            closePlaylist={closePlaylist}
            track={currentTrack}
            togglePlay={togglePlay}
            isPlaying={isPlaying}
            playlistCount={playlist.length}
            prevTrack={prevTrack}
            nextTrack={nextTrack}
          />,
          audioPlayerRef.current,
        )}
    </AudioPlayerContext.Provider>
  );
};

export const AudioPlayer = ({
  playlistCount,
  track,
  closePlaylist,
  prevTrack,
  togglePlay,
  isPlaying,
  nextTrack,
}: {
  track: Track;
  playlistCount: number;
  closePlaylist: () => void;
  togglePlay: () => void;
  isPlaying: boolean;
  prevTrack: () => void;
  nextTrack: () => void;
}) => {
  return (
    <div data-testid="audio-player" className="px-3 bg-grey-90 text-white">
      <div className="flex justify-between md:justify-center gap-4 relative py-1">
        <div className="flex flex-col justify-center items-center">
          <div className="flex items-center ">
            <button
              data-testid="player-prev-track"
              onClick={prevTrack}
              className="cursor-pointer transition-colors ease-out duration-0.15 text-white hover:text-grey-40"
            >
              <NIAudioPreviousIcon />
            </button>

            <button
              data-testid={isPlaying ? 'player-on-play' : 'player-on-pause'}
              onClick={togglePlay}
              className="cursor-pointer transition-colors ease-out duration-0.15 text-white hover:text-grey-40"
            >
              {isPlaying ? <NIAudioPauseIcon /> : <NIAudioPlayIcon />}
            </button>

            <button
              data-testid="player-next-track"
              onClick={nextTrack}
              className="cursor-pointer transition-colors ease-out duration-0.15 text-white hover:text-grey-40"
            >
              <NIAudioNextIcon />
            </button>
          </div>

          <div className="flex justify-center items-center text-2xs space-x-1 md:hidden">
            <span>
              {track.positionInPlaylist} / {playlistCount}
            </span>

            <span data-testid="player-current-time">{track.currentTime}</span>
          </div>
        </div>

        <div className="hidden md:block w-full max-w-[800px] flex justify-center items-center">
          <div
            className={clsx(
              'flex items-center justify-center',
              !track.description && 'h-full',
            )}
          >
            <span data-testid="player-track-title" className="text-xs mr-4">
              {track.title}
            </span>
            <span data-testid="player-track-count" className="text-2xs">
              {track.positionInPlaylist} / {playlistCount}
            </span>
          </div>
          {track.description && (
            <div
              data-testid="player-track-desc"
              className="text-2xs overflow-hidden line-clamp-2"
            >
              {track.description}
            </div>
          )}
        </div>

        <button
          data-testid="player-close"
          onClick={closePlaylist}
          className="cursor-pointer place-items-end transition-colors ease-out duration-0.15 text-white hover:text-grey-40"
        >
          <NIAudioCloseIcon />
        </button>
      </div>
    </div>
  );
};
